import React, { useState, useEffect, useRef } from 'react';
import SidebarLayout from '../../components/SidebarLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import EditTablePage from '../EditTablePage';
import CreateTableForm from '../CreateTablePage/CreateTableForm';
import ErpDataGrid from '../../components/ErpDataGrid';
import { fileService } from '../../services/api';
import styles from './TemplatesPage.module.css';
import '../CreateTablePage/styles.css';
import './TemplatesPageDeleteModal.css';
import TemplateActivityLogsPanel from './TemplateActivityLogsPanel';
import MasterImportModal from './MasterImportModal';
import AddFromMasterModal from './AddFromMasterModal';
import { apiFetch, parseJsonResponse } from '../../utils/httpClient';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

const ANALYTICS_ERP_TABLES = [
  { id: 'analytics:Academics', name: 'Academics', analyticsTable: 'Academics', isAnalyticsTable: true },
  { id: 'analytics:Attendance', name: 'Attendance', analyticsTable: 'Attendance', isAnalyticsTable: true },
  { id: 'analytics:Admissions', name: 'Admissions', analyticsTable: 'Admissions', isAnalyticsTable: true },
  { id: 'analytics:Receivables', name: 'Receivables', analyticsTable: 'Receivables', isAnalyticsTable: true },
  { id: 'analytics:Payments', name: 'Payments', analyticsTable: 'Payments', isAnalyticsTable: true },
];

const TemplatesPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { showError, showSuccess } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeAction, setActiveAction] = useState(null); // 'view', 'track', etc.
  const [viewSchemaId, setViewSchemaId] = useState(null);
  const [viewMode, setViewMode] = useState('data'); // 'data' | 'edit-fields'
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [rowSelectMode, setRowSelectMode] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [rowDeleteModalOpen, setRowDeleteModalOpen] = useState(false);
  const [deletingRows, setDeletingRows] = useState(false);
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [shouldScrollToView, setShouldScrollToView] = useState(false);
  
  // Refs for scrolling
  const viewDataSectionRef = useRef(null);
  const trackSectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const analyticsTemplateInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (activeAction === 'view' && viewDataSectionRef.current && shouldScrollToView) {
      setTimeout(() => {
        viewDataSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
        setShouldScrollToView(false);
      }, 200);
    }
  }, [activeAction, viewSchemaId, shouldScrollToView]);

  useEffect(() => {
    if (activeAction === 'track' && trackSectionRef.current && !loadingLogs) {
      setTimeout(() => {
        trackSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 200);
    }
  }, [activeAction, loadingLogs]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/visuals/schemas', {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      const data = await parseJsonResponse(response, 'Failed to fetch templates');
      const schemas = data.data || [];

      // Map schemas to templates format
      const mappedTemplates = schemas.map((schema) => {
        // Parse schema_definition if it's a string
        let schemaDef = schema.schema_definition;
        if (typeof schemaDef === 'string') {
          try {
            schemaDef = JSON.parse(schemaDef);
          } catch (e) {
            schemaDef = {};
          }
        }

        // Extract product tiers from schema_definition or default to ['Beta']
        let productTier = ['Beta'];
        if (schemaDef?.product_tier) {
          // If product_tier is an array, use it; if string, convert to array
          productTier = Array.isArray(schemaDef.product_tier)
            ? schemaDef.product_tier
            : [schemaDef.product_tier];
        } else if (schemaDef?.productTier) {
          productTier = Array.isArray(schemaDef.productTier)
            ? schemaDef.productTier
            : [schemaDef.productTier];
        }

        // Get last modified date from schema_definition or use created_at
        const lastModified = schemaDef?.updated_at || schema.created_at;

        let creator = 'Unknown';
        if (schema.creator_role) {
          if (schema.creator_role === 'admin') {
            creator = 'AnalyticaX';
          } else {
            creator = user?.fullName || user?.username || 'Unknown';
          }
        }

        return {
          id: schema.schema_id,
          name: schema.table_name,
          creator: creator,
          productTier: productTier,
          created: schema.created_at,
          lastModified: lastModified,
          version: schema.version,
          schemaDefinition: schemaDef,
          isOrgTemplate: schema.is_org_template === true || schema.is_org_template === 'true',
          isMaster:
            schema.is_master === true ||
            schema.is_master === 'true' ||
            schema.is_master === 1 ||
            schema.is_master === 't' ||
            schemaDef?.is_master === true ||
            schemaDef?.is_master === 'true',
        };
      });
      setTemplates(mappedTemplates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
      showError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handlers for action buttons
  const handleView = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      resetRowSelection();
      setSelectedTemplate(template);
      setActiveAction('view');
      setViewSchemaId(templateId);
      setViewMode('data');
      setShouldScrollToView(true);
    }
  };

  const handleDownload = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      
      try {
        // Show loading state
        // showError(''); // Clear any previous errors
        
        const query = template.isOrgTemplate ? '?is_org_template=true' : '';
        const response = await fetch(`${API_URL}/schemas/${templateId}/download-template${query}`, {
          method: 'GET',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to download template' }));
          throw new Error(errorData.message || 'Failed to download template');
        }

        // Get the blob from response
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${template.name}_Template.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('Template downloaded successfully');
      } catch (err) {
        console.error('Error downloading template:', err);
        showError(err.message || 'Failed to download template');
      }
    }
  };

  const handleAnalyze = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setAnalyzeModalOpen(true);
      setUploadResult(null);
      setUploadProgress(null);
    }
  };

  const closeAnalyzeModal = () => {
    if (!uploading) {
      setAnalyzeModalOpen(false);
      setUploadResult(null);
      setUploadProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedTemplate) return;

    // Validate file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      showError('Invalid file type. Please upload an Excel file (.xlsx, .xls) or CSV file.');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      showError('File size exceeds 10MB limit.');
      return;
    }

    setUploading(true);
    setUploadProgress('Uploading and validating file...');
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const query = selectedTemplate.isOrgTemplate ? '?is_org_template=true' : '';
      const response = await fetch(`${API_URL}/schemas/${selectedTemplate.id}/analyze-upload${query}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
          // Don't set Content-Type, let browser set it with boundary for FormData
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUploadResult({
          success: true,
          message: result.message,
          insertedCount: result.insertedCount,
          totalRows: result.totalRows,
          acceptedRows: result.acceptedRows,
          failedRows: result.failedRows,
          successRate: result.successRate
        });
        showSuccess(result.message);
        // Refresh templates list
        await fetchTemplates();
      } else {
        setUploadResult({
          success: false,
          message: result.message,
          totalRows: result.totalRows,
          acceptedRows: result.acceptedRows,
          failedRows: result.failedRows,
          successRate: result.successRate,
          errors: result.errors || []
        });
        showError(result.message);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      showError(err.message || 'Failed to upload and analyze file');
      setUploadResult({
        success: false,
        message: err.message || 'Failed to upload and analyze file'
      });
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleTrack = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setActiveAction('track');
      await fetchActivityLogs(templateId);
    }
  };

  const fetchActivityLogs = async (schemaId) => {
    try {
      setLoadingLogs(true);
      // Pass schema_id to backend for filtering
      const logs = await fileService.getActivityLogs(schemaId);
      setActivityLogs(logs);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      // showError('Failed to load activity logs');
      setActivityLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const openDeleteModal = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template?.isOrgTemplate) {
      showError('Organization templates cannot be deleted.');
      return;
    }
    if (template) {
      setTemplateToDelete(template);
      setDeleteConfirmText('');
      setDeleteModalOpen(true);
    }
  };

  const closeDeleteModal = () => {
    if (!deleting) {
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
      setDeleteConfirmText('');
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    
    if (deleteConfirmText !== `Delete ${templateToDelete.name}`) {
      return;
    }

    setDeleting(true);
    try {
      const query = templateToDelete.isOrgTemplate ? '?is_org_template=true' : '';
      const response = await fetch(`${API_URL}/schemas/${templateToDelete.id}${query}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete template');
      }

      showSuccess('Table deleted successfully');
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
      setDeleteConfirmText('');
      setSelectedTemplate(null);
      setActiveAction(null);
      setHasAutoSelected(false);
      setShouldScrollToView(false);
      
      // Refresh templates list
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      showError(err.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const resetRowSelection = () => {
    setRowSelectMode(false);
    setSelectedRowIds([]);
    setRowDeleteModalOpen(false);
  };

  const handleDeleteRowsClick = () => {
    if (viewMode !== 'data') return;
    if (!rowSelectMode) {
      setRowSelectMode(true);
      setSelectedRowIds([]);
      return;
    }
    if (selectedRowIds.length === 0) {
      showError('Select at least one row to delete');
      return;
    }
    setRowDeleteModalOpen(true);
  };

  const handleConfirmDeleteRows = async () => {
    if (!viewSchemaId || selectedRowIds.length === 0) return;
    setDeletingRows(true);
    try {
      const response = await fetch(`${API_URL}/visuals/data/delete`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          schema_id: viewSchemaId,
          record_ids: selectedRowIds,
        }),
      });
      const data = await parseJsonResponse(response, 'Failed to delete rows');
      const deletedCount = data.deletedCount ?? selectedRowIds.length;
      const failedIds = data.failedIds || [];
      if (failedIds.length > 0) {
        showError(`Deleted ${deletedCount}, failed ${failedIds.length}`);
      } else {
        showSuccess(
          `Deleted ${deletedCount} ${deletedCount === 1 ? 'row' : 'rows'}`
        );
      }
      resetRowSelection();
      setGridRefreshKey((key) => key + 1);
    } catch (err) {
      showError(err.message || 'Failed to delete rows');
    } finally {
      setDeletingRows(false);
    }
  };

  const downloadAnalyticsTemplate = async () => {
    if (!selectedTemplate?.analyticsTable) return;
    try {
      const response = await fetch(
        `${API_URL}/visuals/analytics-tables/${encodeURIComponent(selectedTemplate.analyticsTable)}/template`,
        { headers: { ...getAuthHeaders() } }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to download template' }));
        throw new Error(errorData.message || 'Failed to download template');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedTemplate.name}_Template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Template downloaded');
    } catch (err) {
      showError(err.message || 'Failed to download template');
    }
  };

  const uploadAnalyticsTemplate = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedTemplate?.analyticsTable) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `${API_URL}/visuals/analytics-tables/${encodeURIComponent(selectedTemplate.analyticsTable)}/upload`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders() },
          body: formData,
        }
      );
      const data = await parseJsonResponse(response, 'Failed to upload template');
      setGridRefreshKey((key) => key + 1);
      showSuccess(data.message || 'Template uploaded');
    } catch (err) {
      showError(err.message || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectTemplate = (templateId) => {
    const template =
      templates.find((t) => t.id === templateId) ||
      ANALYTICS_ERP_TABLES.find((t) => t.id === templateId);
    if (template) {
      resetRowSelection();
      setSelectedTemplate(template);
      setActiveAction('view');
      setViewSchemaId(templateId);
      setViewMode('data');
      setShouldScrollToView(false);
    }
  };

  const handleAddNewTemplate = () => {
    resetRowSelection();
    setActiveAction('create');
    setSelectedTemplate(null);
    setViewSchemaId(null);
    setViewMode('data');
  };

  const handleAddNewMaster = () => {
    resetRowSelection();
    setActiveAction('create-master');
    setSelectedTemplate(null);
    setViewSchemaId(null);
    setViewMode('data');
  };

  const handleCancelCreate = () => {
    if (templates.length > 0 || ANALYTICS_ERP_TABLES.length > 0) {
      const template =
        selectedTemplate ||
        userPageTemplates[0] ||
        userMasterTemplates[0] ||
        ANALYTICS_ERP_TABLES[0];
      setSelectedTemplate(template);
      setActiveAction('view');
      setViewSchemaId(template.id);
      setViewMode('data');
      return;
    }

    setActiveAction(null);
    setSelectedTemplate(null);
    setViewSchemaId(null);
  };

  const handleCreateSuccess = async (createdSchema, isMaster = false) => {
    showSuccess(isMaster ? 'Master table created successfully!' : 'Page created successfully!');
    const newSchemaId = createdSchema?.schema_id;
    await fetchTemplates();

    if (newSchemaId) {
      setActiveAction('view');
      setViewSchemaId(newSchemaId);
      setViewMode('data');
    } else {
      setActiveAction(null);
    }
  };

  const masterTemplates = templates.filter((template) => template.isMaster);
  const pageTemplates = templates.filter((template) => !template.isMaster);
  const userMasterTemplates = masterTemplates.filter((template) => !template.isOrgTemplate);
  const userPageTemplates = pageTemplates.filter((template) => !template.isOrgTemplate);

  const masterSidebarItems = userMasterTemplates.map((template) => ({
    id: template.id,
    label: template.name,
  }));

  const pageSidebarItems = userPageTemplates.map((template) => ({
    id: template.id,
    label: template.name,
  }));

  const analyticsSidebarItems = ANALYTICS_ERP_TABLES.map((template) => ({
    id: template.id,
    label: template.name,
  }));

  const erpSidebarSections = [
    {
      id: 'master-data',
      label: 'Master Data',
      items: masterSidebarItems,
      addNewLabel: 'New master table',
      onAddNew: handleAddNewMaster,
      onSelect: handleSelectTemplate,
      loading,
    },
    {
      id: 'your-pages',
      label: 'Your Pages',
      items: pageSidebarItems,
      addNewLabel: 'New page',
      onAddNew: handleAddNewTemplate,
      onSelect: handleSelectTemplate,
      loading,
    },
    {
      id: 'org-templates',
      label: 'AnalyticaX Org Templates',
      items: analyticsSidebarItems,
      onSelect: handleSelectTemplate,
    },
  ];

  useEffect(() => {
    if (
      !hasAutoSelected &&
      !loading &&
      activeAction !== 'create' &&
      activeAction !== 'create-master'
    ) {
      const pages = templates.filter((t) => !t.isMaster && !t.isOrgTemplate);
      const masters = templates.filter((t) => t.isMaster && !t.isOrgTemplate);
      const firstTemplate = pages[0] || masters[0] || ANALYTICS_ERP_TABLES[0];
      if (!firstTemplate) return;
      setSelectedTemplate(firstTemplate);
      setActiveAction('view');
      setViewSchemaId(firstTemplate.id);
      setViewMode('data');
      setShouldScrollToView(false);
      setHasAutoSelected(true);
    }
  }, [templates, loading, hasAutoSelected, activeAction]);

  useEffect(() => {
    if (activeAction !== 'view' || !viewSchemaId) return;
    const template = templates.find((t) => t.id === viewSchemaId);
    if (template && template.id !== selectedTemplate?.id) {
      setSelectedTemplate(template);
    }
  }, [templates, activeAction, viewSchemaId, selectedTemplate?.id]);

  const hasAnyTemplates = templates.some((template) => !template.isOrgTemplate) || ANALYTICS_ERP_TABLES.length > 0;
  const isCreating = activeAction === 'create' || activeAction === 'create-master';

  const renderCreatePage = () => (
    <CreateTableForm
      key={activeAction}
      isInline
      mode={activeAction === 'create-master' ? 'master' : 'page'}
      masterOptions={masterTemplates.map((template) => ({
        id: template.id,
        label: template.name,
      }))}
      onCancel={handleCancelCreate}
      onSuccess={(createdSchema) =>
        handleCreateSuccess(createdSchema, activeAction === 'create-master')
      }
    />
  );

  const renderErpContent = () => {
    if (loading) {
      return (
        <div className={styles.erpContentEmpty}>
          <div className={styles.loadingSpinner} />
          <p>Loading pages...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.erpContentEmpty}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchTemplates} className={styles.retryButton}>
            Retry
          </button>
        </div>
      );
    }

    if (isCreating) {
      return renderCreatePage();
    }

    if (!hasAnyTemplates) {
      return (
        <div className={styles.erpContentEmpty}>
          <h3>No pages yet</h3>
          <p>Create your first ERP page to digitize operations and onboard data.</p>
          <button onClick={handleAddNewTemplate} className={styles.addFirstButton}>
            + Create your first page
          </button>
        </div>
      );
    }

    if (!selectedTemplate) {
      return (
        <div className={styles.erpContentEmpty}>
          <p>Select a page from the sidebar to view or manage it.</p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.erpContentTop}>
          <div>
            <h3 className={styles.erpPageTitle}>{selectedTemplate.name}</h3>
            <p className={styles.erpPageMeta}>
              {selectedTemplate.isAnalyticsTable
                ? 'AnalyticaX Org Template'
                : selectedTemplate.isMaster
                  ? 'Master table'
                  : 'Page'}
              {selectedTemplate.lastModified
                ? ` · Updated ${formatDate(selectedTemplate.lastModified)}`
                : ''}
            </p>
          </div>
          <div className={styles.erpActionBar}>
            {selectedTemplate.isAnalyticsTable && viewMode === 'data' && (
              <>
                <button className={styles.erpActionButton} onClick={downloadAnalyticsTemplate}>
                  Download template
                </button>
                <button
                  className={styles.erpActionButton}
                  onClick={() => analyticsTemplateInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload template'}
                </button>
                <input
                  ref={analyticsTemplateInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={uploadAnalyticsTemplate}
                />
              </>
            )}
            {!selectedTemplate.isAnalyticsTable &&
              !selectedTemplate.isOrgTemplate &&
              viewMode === 'data' && (
              <button
                className={styles.erpActionButton}
                onClick={() => setImportModalOpen(true)}
              >
                Import
              </button>
            )}
            {!selectedTemplate.isAnalyticsTable &&
              !selectedTemplate.isMaster &&
              viewMode === 'data' &&
              (selectedTemplate.schemaDefinition?.columns || []).some(
                (col) => col.type === 'reference' && col.ref_schema_id
              ) && (
                <button
                  className={styles.erpActionButton}
                  onClick={() => setRosterModalOpen(true)}
                >
                  Add from Class
                </button>
              )}
            {!selectedTemplate.isAnalyticsTable && !selectedTemplate.isOrgTemplate && (
              <button
                className={`${styles.erpActionButton} ${
                  viewMode === 'edit-fields' ? styles.erpActionButtonActive : ''
                }`}
                onClick={() => {
                  resetRowSelection();
                  setActiveAction('view');
                  setViewSchemaId(selectedTemplate.id);
                  setViewMode('edit-fields');
                }}
              >
                Edit fields
              </button>
            )}
            {!selectedTemplate.isAnalyticsTable && viewMode === 'data' && (
              <>
                {rowSelectMode && (
                  <button
                    className={styles.erpActionButton}
                    onClick={resetRowSelection}
                    disabled={deletingRows}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className={`${styles.erpActionButton} ${styles.erpActionButtonDanger}`}
                  onClick={handleDeleteRowsClick}
                  disabled={deletingRows}
                >
                  {deletingRows
                    ? 'Deleting...'
                    : rowSelectMode && selectedRowIds.length > 0
                      ? `Delete rows (${selectedRowIds.length})`
                      : 'Delete rows'}
                </button>
              </>
            )}
            {/* <button className={styles.erpActionButton} onClick={() => handleDownload(selectedTemplate.id)}>
              Download
            </button>
            <button className={styles.erpActionButton} onClick={() => handleAnalyze(selectedTemplate.id)}>
              Analyze
            </button>
            <button className={styles.erpActionButton} onClick={() => handleTrack(selectedTemplate.id)}>
              Track
            </button> */}
            {!selectedTemplate.isAnalyticsTable && !selectedTemplate.isOrgTemplate && (
              <button
                className={`${styles.erpActionButton} ${styles.erpActionButtonDanger}`}
                onClick={() => openDeleteModal(selectedTemplate.id)}
              >
                Delete Table
              </button>
            )}
          </div>
        </div>

        {activeAction === 'view' && viewSchemaId && (
          <div ref={viewDataSectionRef} className={styles.viewDataSection}>
            {viewMode === 'edit-fields' && !selectedTemplate?.isOrgTemplate && !selectedTemplate?.isAnalyticsTable ? (
              <EditTablePage
                schemaId={viewSchemaId}
                isOrgTemplate={false}
                isInline={true}
                onClose={async () => {
                  setViewMode('data');
                  setGridRefreshKey((key) => key + 1);
                  await fetchTemplates();
                }}
                onDeleted={async () => {
                  setViewMode('data');
                  setSelectedTemplate(null);
                  setActiveAction(null);
                  setViewSchemaId(null);
                  setHasAutoSelected(false);
                  await fetchTemplates();
                }}
              />
            ) : (
              <ErpDataGrid
                key={`${viewSchemaId}-${gridRefreshKey}`}
                schemaId={selectedTemplate?.isAnalyticsTable ? undefined : viewSchemaId}
                analyticsTable={selectedTemplate?.analyticsTable || null}
                readOnly={selectedTemplate?.isAnalyticsTable === true}
                isOrgTemplate={selectedTemplate?.isOrgTemplate === true}
                selectMode={rowSelectMode}
                selectedIds={selectedRowIds}
                onSelectedIdsChange={setSelectedRowIds}
                onOpenEditFields={
                  selectedTemplate?.isOrgTemplate || selectedTemplate?.isAnalyticsTable
                    ? undefined
                    : () => {
                        resetRowSelection();
                        setViewMode('edit-fields');
                      }
                }
                onSelectMaster={(masterSchemaId) => {
                  const master = templates.find((t) => t.id === masterSchemaId);
                  if (master) {
                    handleSelectTemplate(master.id);
                  }
                }}
              />
            )}
          </div>
        )}

        {/* {activeAction === 'track' && selectedTemplate && (
          <TemplateActivityLogsPanel
            ref={trackSectionRef}
            templateName={selectedTemplate.name}
            logs={activityLogs}
            isLoading={loadingLogs}
            formatDate={formatDate}
            onRefresh={() => fetchActivityLogs(selectedTemplate.id)}
            onClose={() => {
              setActiveAction(null);
              setActivityLogs([]);
            }}
          />
        )} */}
      </>
    );
  };

  return (
    <SidebarLayout
      nestedNav={{
        parentId: 'erp',
        activeId: isCreating ? null : selectedTemplate?.id,
        sections: erpSidebarSections,
      }}
    >
      <div className={styles.templatesPage}>
        <div className={styles.container}>
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>ERP</h2>
          </div>

          <div className={styles.erpLayout}>
            <div className={styles.erpContent}>
              {renderErpContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {/* Analyze Upload Modal */}
      {analyzeModalOpen && selectedTemplate && (
        <div className={styles.modalOverlay} onClick={closeAnalyzeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Analyze & Upload Template: {selectedTemplate.name}</h3>
              <button
                className={styles.closeModalButton}
                onClick={closeAnalyzeModal}
                disabled={uploading}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalText}>
                Upload an Excel file to validate against the schema and import data.
              </p>
              
              {!uploadResult && (
                <div className={styles.uploadSection}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className={styles.fileInput}
                    id="file-upload-input"
                  />
                  <label htmlFor="file-upload-input" className={styles.fileInputLabel}>
                    {uploading ? 'Processing...' : 'Choose Excel File'}
                  </label>
                  <p className={styles.fileHint}>Supported formats: .xlsx, .xls, .csv (Max 10MB)</p>
                </div>
              )}

              {uploadProgress && (
                <div className={styles.uploadProgress}>
                  <div className={styles.progressSpinner}></div>
                  <p>{uploadProgress}</p>
                </div>
              )}

              {uploadResult && (
                <div className={`${styles.uploadResult} ${uploadResult.success ? styles.success : styles.error}`}>
                  <h4>{uploadResult.success ? '✓ Upload Successful' : '✗ Upload Failed'}</h4>
                  <p className={styles.resultMessage}>{uploadResult.message}</p>
                  
                  {uploadResult.totalRows !== undefined && (
                    <div className={styles.resultStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total Rows:</span>
                        <span className={styles.statValue}>{uploadResult.totalRows}</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Accepted:</span>
                        <span className={`${styles.statValue} ${styles.successText}`}>
                          {uploadResult.acceptedRows}
                        </span>
                      </div>
                      {uploadResult.failedRows > 0 && (
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Failed:</span>
                          <span className={`${styles.statValue} ${styles.errorText}`}>
                            {uploadResult.failedRows}
                          </span>
                        </div>
                      )}
                      {uploadResult.successRate !== undefined && (
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Success Rate:</span>
                          <span className={styles.statValue}>
                            {uploadResult.successRate.toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className={styles.errorDetails}>
                      <h5>Validation Errors:</h5>
                      <div className={styles.errorList}>
                        {uploadResult.errors.slice(0, 10).map((error, index) => (
                          <div key={index} className={styles.errorItem}>
                            <strong>Row {error.row}:</strong> {error.errors.join(', ')}
                          </div>
                        ))}
                        {uploadResult.errors.length > 10 && (
                          <p className={styles.moreErrors}>
                            ... and {uploadResult.errors.length - 10} more error(s)
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={closeAnalyzeModal}
                disabled={uploading}
              >
                {uploadResult ? 'Close' : 'Cancel'}
              </button>
              {uploadResult && !uploading && (
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => {
                    setUploadResult(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Upload Another File
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && templateToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete table &quot;{templateToDelete.name}&quot;</h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                This action cannot be undone. This will permanently mark the table as deleted for
                your school.
              </p>
              <p className="modal-subtext">Please type <strong style={{ color: '#000', fontWeight: 'bold' }}>Delete {templateToDelete.name}</strong> to confirm.</p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={`Type Delete ${templateToDelete.name} to confirm`}
                className={`confirm-input ${deleteConfirmText === `Delete ${templateToDelete.name}` ? 'valid' : deleteConfirmText ? 'invalid' : ''}`}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="cancel-btn" onClick={closeDeleteModal} disabled={deleting}>Cancel</button>
              <button
                type="button"
                className="danger-btn"
                disabled={deleting || deleteConfirmText !== `Delete ${templateToDelete.name}`}
                onClick={handleDelete}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rowDeleteModalOpen && (
        <div className="modal-overlay" onClick={() => !deletingRows && setRowDeleteModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                Delete {selectedRowIds.length}{' '}
                {selectedRowIds.length === 1 ? 'row' : 'rows'}
                {selectedTemplate?.name ? ` from "${selectedTemplate.name}"` : ''}
              </h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                This cannot be undone. The selected {selectedRowIds.length === 1 ? 'row' : 'rows'} will
                be removed from this table.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setRowDeleteModalOpen(false)}
                disabled={deletingRows}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-btn"
                disabled={deletingRows || selectedRowIds.length === 0}
                onClick={handleConfirmDeleteRows}
              >
                {deletingRows ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {importModalOpen &&
        selectedTemplate &&
        !selectedTemplate.isAnalyticsTable &&
        !selectedTemplate.isOrgTemplate && (
        <MasterImportModal
          template={selectedTemplate}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setImportModalOpen(false)}
          onImported={(result) => {
            showSuccess(result.message || 'Import completed');
            setGridRefreshKey((key) => key + 1);
          }}
        />
      )}

      {rosterModalOpen &&
        selectedTemplate &&
        !selectedTemplate.isMaster && (
          <AddFromMasterModal
            template={selectedTemplate}
            getAuthHeaders={getAuthHeaders}
            onClose={() => setRosterModalOpen(false)}
            onAdded={(result) => {
              showSuccess(result.message || 'Rows added');
              setGridRefreshKey((key) => key + 1);
            }}
          />
        )}
    </SidebarLayout>
  );
};

export default TemplatesPage;

